/**
 * Comprehensive Tracking Events for Customer Activities & Leads
 * Tracks: Page views, Product views, Add to cart, Checkout, Leads, Form submissions, etc.
 * 
 * IMPORTANT: This system avoids duplicate tracking by:
 * 1. Not tracking PageView (already tracked by Facebook Pixel init)
 * 2. Checking if events were already sent before sending again
 * 3. Including customer ID for proper identification
 */

(function() {
  'use strict';

  // Initialize tracking object
  window.MyraTracking = window.MyraTracking || {};

  // Facebook Pixel ID
  const FB_PIXEL_ID = '1177951943999410';
  
  // Track which events have been sent to avoid duplicates
  const sentEvents = new Set();
  
  /**
   * Get customer ID from Shopify
   */
  function getCustomerId() {
    // Try to get from Shopify object
    if (window.Shopify && window.Shopify.customer && window.Shopify.customer.id) {
      return window.Shopify.customer.id;
    }
    
    // Try to get from localStorage (set during checkout)
    const storedCustomerId = localStorage.getItem('myra_customer_id');
    if (storedCustomerId) {
      return storedCustomerId;
    }
    
    // Generate anonymous ID if not logged in
    let anonId = localStorage.getItem('myra_anon_id');
    if (!anonId) {
      anonId = 'anon_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('myra_anon_id', anonId);
    }
    return anonId;
  }
  
  /**
   * Check if event was already sent to avoid duplicates
   */
  function isEventAlreadySent(eventKey) {
    return sentEvents.has(eventKey);
  }
  
  /**
   * Mark event as sent
   */
  function markEventAsSent(eventKey) {
    sentEvents.add(eventKey);
  }

  /**
   * Track Page View
   * SKIPPED: PageView is already tracked by Facebook Pixel init in theme.liquid
   */
  function trackPageView() {
    // PageView is already tracked by: fbq('init', '1177951943999410'); fbq('track', 'PageView');
    // No need to track again to avoid duplicates
    console.log('ℹ️ PageView tracking skipped (already tracked by Pixel init)');
  }

  /**
   * Track Product View
   */
  function trackProductView(productData) {
    const eventKey = `product_view_${productData.id}`;
    
    // Avoid duplicate tracking
    if (isEventAlreadySent(eventKey)) {
      console.log('⚠️ Product View already tracked:', productData.id);
      return;
    }
    
    const customerId = getCustomerId();
    const data = {
      content_name: productData.title || document.querySelector('h1')?.innerText,
      content_ids: [productData.id],
      content_type: 'product',
      value: productData.price || 0,
      currency: window.FoxThemeSettings?.currency_code || 'INR',
      customer_id: customerId,
      timestamp: new Date().toISOString()
    };

    // Facebook Pixel
    if (window.fbq) {
      fbq('track', 'ViewContent', {
        content_name: data.content_name,
        content_ids: data.content_ids,
        content_type: data.content_type,
        value: data.value,
        currency: data.currency,
        user_data: {
          em: customerId
        }
      });
    }

    // Google Analytics
    if (window.gtag) {
      gtag('event', 'view_item', {
        items: [{
          item_id: productData.id,
          item_name: data.content_name,
          price: data.value,
          currency: data.currency
        }],
        user_id: customerId
      });
    }

    markEventAsSent(eventKey);
    console.log('🛍️ Product View Tracked:', data);
  }

  /**
   * Track Add to Cart
   */
  function trackAddToCart(productData) {
    const eventKey = `add_to_cart_${productData.id}_${Date.now()}`;
    const customerId = getCustomerId();
    
    const data = {
      content_name: productData.title,
      content_ids: [productData.id],
      content_type: 'product',
      value: productData.price,
      currency: window.FoxThemeSettings?.currency_code || 'INR',
      quantity: productData.quantity || 1,
      customer_id: customerId,
      timestamp: new Date().toISOString()
    };

    // Facebook Pixel
    if (window.fbq) {
      fbq('track', 'AddToCart', {
        content_name: data.content_name,
        content_ids: data.content_ids,
        content_type: data.content_type,
        value: data.value,
        currency: data.currency
      });
    }

    // Google Analytics
    if (window.gtag) {
      gtag('event', 'add_to_cart', {
        items: [{
          item_id: productData.id,
          item_name: data.content_name,
          price: data.value,
          quantity: data.quantity,
          currency: data.currency
        }],
        user_id: customerId
      });
    }

    markEventAsSent(eventKey);
    console.log('🛒 Add to Cart Tracked:', data);
  }

  /**
   * Track Initiate Checkout
   */
  function trackInitiateCheckout(cartData) {
    const data = {
      value: cartData.total || 0,
      currency: window.FoxThemeSettings?.currency_code || 'INR',
      num_items: cartData.item_count || 0,
      timestamp: new Date().toISOString()
    };

    // Facebook Pixel
    if (window.fbq) {
      fbq('track', 'InitiateCheckout', data);
    }

    // Google Analytics
    if (window.gtag) {
      gtag('event', 'begin_checkout', {
        value: data.value,
        currency: data.currency,
        items: cartData.items || []
      });
    }

    console.log('💳 Initiate Checkout Tracked:', data);
  }

  /**
   * Track Lead (Form Submission, Contact Form, Newsletter, etc.)
   */
  function trackLead(leadData) {
    const eventKey = `lead_${leadData.email || 'unknown'}_${Date.now()}`;
    const customerId = getCustomerId();
    
    const data = {
      lead_type: leadData.type || 'general', // 'contact_form', 'newsletter', 'ring_sizer', 'consultation', etc.
      email: leadData.email || '',
      phone: leadData.phone || '',
      name: leadData.name || '',
      message: leadData.message || '',
      customer_id: customerId,
      timestamp: new Date().toISOString()
    };

    // Facebook Pixel
    if (window.fbq) {
      fbq('track', 'Lead', {
        content_name: data.lead_type,
        content_category: 'lead',
        value: 0,
        currency: window.FoxThemeSettings?.currency_code || 'INR',
        user_data: {
          em: data.email,
          ph: data.phone
        }
      });
    }

    // Google Analytics
    if (window.gtag) {
      gtag('event', 'generate_lead', {
        lead_type: data.lead_type,
        value: 0,
        currency: window.FoxThemeSettings?.currency_code || 'INR',
        user_id: customerId
      });
    }

    // Store lead data for CRM integration
    storeLead(data);
    
    // Store customer ID if email provided
    if (data.email) {
      localStorage.setItem('myra_customer_id', customerId);
    }

    markEventAsSent(eventKey);
    console.log('📝 Lead Tracked:', data);
  }

  /**
   * Track Search
   */
  function trackSearch(searchQuery) {
    const data = {
      search_term: searchQuery,
      timestamp: new Date().toISOString()
    };

    // Facebook Pixel
    if (window.fbq) {
      fbq('track', 'Search', {
        search_string: searchQuery
      });
    }

    // Google Analytics
    if (window.gtag) {
      gtag('event', 'search', {
        search_term: searchQuery
      });
    }

    console.log('🔍 Search Tracked:', data);
  }

  /**
   * Track Filter/Browse
   */
  function trackFilter(filterData) {
    const data = {
      filter_type: filterData.type, // 'price', 'category', 'gemstone', 'carat', etc.
      filter_value: filterData.value,
      timestamp: new Date().toISOString()
    };

    // Google Analytics
    if (window.gtag) {
      gtag('event', 'view_item_list', {
        items: [{
          item_category: data.filter_type,
          item_name: data.filter_value
        }]
      });
    }

    console.log('🎯 Filter Tracked:', data);
  }

  /**
   * Track Button Click
   */
  function trackButtonClick(buttonData) {
    const data = {
      button_name: buttonData.name,
      button_type: buttonData.type, // 'cta', 'contact', 'consultation', etc.
      page_url: window.location.href,
      timestamp: new Date().toISOString()
    };

    // Google Analytics
    if (window.gtag) {
      gtag('event', 'click', {
        element_name: data.button_name,
        element_type: data.button_type
      });
    }

    console.log('🔘 Button Click Tracked:', data);
  }

  /**
   * Track Video View
   */
  function trackVideoView(videoData) {
    const data = {
      video_title: videoData.title,
      video_url: videoData.url,
      timestamp: new Date().toISOString()
    };

    // Facebook Pixel
    if (window.fbq) {
      fbq('track', 'ViewContent', {
        content_name: data.video_title,
        content_type: 'video'
      });
    }

    // Google Analytics
    if (window.gtag) {
      gtag('event', 'video_start', {
        video_title: data.video_title
      });
    }

    console.log('🎥 Video View Tracked:', data);
  }

  /**
   * Track Consultation Request
   */
  function trackConsultationRequest(consultationData) {
    const data = {
      consultation_type: consultationData.type || 'general',
      customer_name: consultationData.name || '',
      customer_email: consultationData.email || '',
      customer_phone: consultationData.phone || '',
      preferred_date: consultationData.date || '',
      timestamp: new Date().toISOString()
    };

    // Facebook Pixel
    if (window.fbq) {
      fbq('track', 'Lead', {
        content_name: 'Consultation Request',
        content_category: 'consultation'
      });
    }

    // Google Analytics
    if (window.gtag) {
      gtag('event', 'generate_lead', {
        lead_type: 'consultation_request',
        value: 0
      });
    }

    storeLead(data);
    console.log('👤 Consultation Request Tracked:', data);
  }

  /**
   * Track Ring Sizer Order
   */
  function trackRingSizerOrder(sizerData) {
    const data = {
      sizer_type: 'ring_sizer',
      customer_name: sizerData.name || '',
      customer_email: sizerData.email || '',
      customer_phone: sizerData.phone || '',
      delivery_address: sizerData.address || '',
      timestamp: new Date().toISOString()
    };

    // Facebook Pixel
    if (window.fbq) {
      fbq('track', 'Lead', {
        content_name: 'Ring Sizer Order',
        content_category: 'ring_sizer'
      });
    }

    // Google Analytics
    if (window.gtag) {
      gtag('event', 'generate_lead', {
        lead_type: 'ring_sizer_order'
      });
    }

    storeLead(data);
    console.log('📏 Ring Sizer Order Tracked:', data);
  }

  /**
   * Track Newsletter Signup
   */
  function trackNewsletterSignup(email) {
    const data = {
      email: email,
      timestamp: new Date().toISOString()
    };

    // Facebook Pixel
    if (window.fbq) {
      fbq('track', 'Lead', {
        content_name: 'Newsletter Signup',
        content_category: 'newsletter'
      });
    }

    // Google Analytics
    if (window.gtag) {
      gtag('event', 'generate_lead', {
        lead_type: 'newsletter_signup'
      });
    }

    storeLead(data);
    console.log('📧 Newsletter Signup Tracked:', data);
  }

  /**
   * Track Video Call - Home Page
   */
  function trackVideoCallHomePage(videoCallData) {
    const eventKey = `video_call_home_${Date.now()}`;
    const customerId = getCustomerId();
    
    const data = {
      video_call_location: 'home_page',
      customer_name: videoCallData.name || '',
      customer_phone: videoCallData.phone || '',
      customer_id: customerId,
      timestamp: new Date().toISOString()
    };

    // Facebook Pixel
    if (window.fbq) {
      fbq('track', 'Lead', {
        content_name: 'Video Call - Home Page',
        content_category: 'video_call'
      });
    }

    // Google Analytics
    if (window.gtag) {
      gtag('event', 'video_call_home_page', {
        event_category: 'engagement',
        event_label: 'home_page',
        user_id: customerId
      });
    }

    storeLead(data);
    markEventAsSent(eventKey);
    console.log('📹 Video Call (Home Page) Tracked:', data);
  }

  /**
   * Track Video Call - Product Detail Page (PDP)
   */
  function trackVideoCallPDP(videoCallData) {
    const eventKey = `video_call_pdp_${videoCallData.product_id || 'unknown'}_${Date.now()}`;
    const customerId = getCustomerId();
    
    const data = {
      video_call_location: 'product_detail_page',
      product_id: videoCallData.product_id || '',
      product_title: videoCallData.product_title || '',
      customer_name: videoCallData.name || '',
      customer_phone: videoCallData.phone || '',
      customer_id: customerId,
      timestamp: new Date().toISOString()
    };

    // Facebook Pixel
    if (window.fbq) {
      fbq('track', 'Lead', {
        content_name: 'Video Call - Product Page',
        content_category: 'video_call',
        content_ids: [videoCallData.product_id]
      });
    }

    // Google Analytics
    if (window.gtag) {
      gtag('event', 'video_call_pdp_page', {
        event_category: 'engagement',
        event_label: 'product_detail_page',
        product_id: videoCallData.product_id,
        product_name: videoCallData.product_title,
        user_id: customerId
      });
    }

    storeLead(data);
    markEventAsSent(eventKey);
    console.log('📹 Video Call (PDP) Tracked:', data);
  }

  /**
   * Track Video Call - Sticky Bar
   */
  function trackVideoCallStickyBar(videoCallData) {
    const eventKey = `video_call_sticky_${videoCallData.product_id || 'unknown'}_${Date.now()}`;
    const customerId = getCustomerId();
    
    const data = {
      video_call_location: 'sticky_bar',
      product_id: videoCallData.product_id || '',
      product_title: videoCallData.product_title || '',
      customer_name: videoCallData.name || '',
      customer_phone: videoCallData.phone || '',
      customer_id: customerId,
      timestamp: new Date().toISOString()
    };

    // Facebook Pixel
    if (window.fbq) {
      fbq('track', 'Lead', {
        content_name: 'Video Call - Sticky Bar',
        content_category: 'video_call',
        content_ids: [videoCallData.product_id]
      });
    }

    // Google Analytics
    if (window.gtag) {
      gtag('event', 'video_call_sticky_bar', {
        event_category: 'engagement',
        event_label: 'sticky_bar',
        product_id: videoCallData.product_id,
        product_name: videoCallData.product_title,
        user_id: customerId
      });
    }

    storeLead(data);
    markEventAsSent(eventKey);
    console.log('📹 Video Call (Sticky Bar) Tracked:', data);
  }

  /**
   * Store Lead Data (for CRM integration)
   */
  function storeLead(leadData) {
    try {
      // Store in localStorage
      const leads = JSON.parse(localStorage.getItem('myra_leads') || '[]');
      leads.push(leadData);
      localStorage.setItem('myra_leads', JSON.stringify(leads));

      // Optional: Send to your backend/CRM
      // fetch('/api/leads', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(leadData)
      // });
    } catch (error) {
      console.error('Error storing lead:', error);
    }
  }

  /**
   * Auto-track Add to Cart from global event
   */
  document.addEventListener('cart:item-added', function(e) {
    if (e.detail && e.detail.product) {
      trackAddToCart({
        id: e.detail.product.id,
        title: e.detail.product.title,
        price: e.detail.product.price,
        quantity: e.detail.quantity || 1
      });
    }
  });

  /**
   * Auto-track form submissions
   */
  document.addEventListener('submit', function(e) {
    const form = e.target;
    const formId = form.id || form.name || 'unknown';

    if (formId.includes('contact') || formId.includes('lead')) {
      const formData = new FormData(form);
      trackLead({
        type: formId,
        name: formData.get('name') || formData.get('full_name') || '',
        email: formData.get('email') || '',
        phone: formData.get('phone') || formData.get('telephone') || '',
        message: formData.get('message') || formData.get('inquiry') || ''
      });
    }

    if (formId.includes('newsletter')) {
      trackNewsletterSignup(formData.get('email'));
    }
  });

  // Expose tracking functions globally
  window.MyraTracking.trackPageView = trackPageView;
  window.MyraTracking.trackProductView = trackProductView;
  window.MyraTracking.trackAddToCart = trackAddToCart;
  window.MyraTracking.trackInitiateCheckout = trackInitiateCheckout;
  window.MyraTracking.trackLead = trackLead;
  window.MyraTracking.trackSearch = trackSearch;
  window.MyraTracking.trackFilter = trackFilter;
  window.MyraTracking.trackButtonClick = trackButtonClick;
  window.MyraTracking.trackVideoView = trackVideoView;
  window.MyraTracking.trackConsultationRequest = trackConsultationRequest;
  window.MyraTracking.trackRingSizerOrder = trackRingSizerOrder;
  window.MyraTracking.trackNewsletterSignup = trackNewsletterSignup;
  window.MyraTracking.trackVideoCallHomePage = trackVideoCallHomePage;
  window.MyraTracking.trackVideoCallPDP = trackVideoCallPDP;
  window.MyraTracking.trackVideoCallStickyBar = trackVideoCallStickyBar;

  /**
   * Cache Management - Refresh cached scripts periodically
   */
  function initializeCacheRefresh() {
    const CACHE_REFRESH_INTERVAL = 7 * 24 * 60 * 60 * 1000; // 7 days
    const CACHE_REFRESH_KEY = 'myra_cache_refresh_timestamp';
    
    const lastRefresh = localStorage.getItem(CACHE_REFRESH_KEY);
    const now = Date.now();
    
    // Check if cache needs refresh
    if (!lastRefresh || (now - parseInt(lastRefresh)) > CACHE_REFRESH_INTERVAL) {
      // Clear Service Worker cache
      if ('caches' in window) {
        caches.keys().then((cacheNames) => {
          cacheNames.forEach((cacheName) => {
            caches.delete(cacheName).then(() => {
              console.log('🗑️ Cleared cache:', cacheName);
            });
          });
        });
      }
      
      // Update refresh timestamp
      localStorage.setItem(CACHE_REFRESH_KEY, now.toString());
      console.log('🔄 Cache refresh scheduled - will reload on next visit');
    }
  }
  
  /**
   * Expose cache functions globally
   */
  window.MyraTracking.refreshCache = function() {
    if ('caches' in window) {
      caches.keys().then((cacheNames) => {
        Promise.all(
          cacheNames.map((cacheName) => caches.delete(cacheName))
        ).then(() => {
          console.log('✅ Cache cleared manually');
          localStorage.removeItem('myra_cache_refresh_timestamp');
          window.location.reload();
        });
      });
    } else {
      console.log('⚠️ Cache API not supported');
    }
  };
  
  window.MyraTracking.getCacheStatus = function() {
    if ('caches' in window) {
      caches.keys().then((cacheNames) => {
        console.log('📊 Cache Status:');
        cacheNames.forEach((cacheName) => {
          caches.open(cacheName).then((cache) => {
            cache.keys().then((requests) => {
              console.log(`  Cache: ${cacheName} (${requests.length} files)`);
              requests.forEach((request) => {
                console.log(`    - ${request.url}`);
              });
            });
          });
        });
      });
    }
  };

  // Track page view on load
  document.addEventListener('DOMContentLoaded', function() {
    trackPageView();
    initializeCacheRefresh();
  });

})();
